/** @format */
import { CustomModuleAttainment, EntityWithRule } from '../../../types/generated/common-backend.js';
import { getModulePlanState, getModulePlanStateLabel, ModulePlanState } from './modulePlanState.js';
import { createRootRule, isStudyRightPersonalized } from './moduleRootRuleCreator.js';
import { PlanValidationResult } from './planValidationResult.js';
import { ValidatablePlan } from './validatablePlan.js';
import { Validation } from './validation.js';

export class PlanValidationTs {
    static validatePlan(validatablePlan: ValidatablePlan): PlanValidationResult {
        const planValidationResult = new PlanValidationResult();
        planValidationResult.validatablePlan = validatablePlan;

        const ruleContext = Validation.validateModule(
            validatablePlan.rootModule,
            validatablePlan,
            planValidationResult,
            PlanValidationTs.resolveCustomRuleIfExists(validatablePlan),
        );
        planValidationResult.rootValidationResult = ruleContext.getResults({ validatablePlan });

        return planValidationResult;
    }

    private static resolveCustomRuleIfExists(validatablePlan: ValidatablePlan) {
        return validatablePlan.studyRight && isStudyRightPersonalized(validatablePlan.studyRight)
            ? createRootRule(validatablePlan.rootModule, validatablePlan.studyRight)
            : undefined;
    }

    /**
     * The validation state of a module or a custom module attainment in the plan, and where it stands with its module content approval.
     * Missing without a module, and when the plan has no validation result for it.
     */
    static getModulePlanState(
        module: EntityWithRule | CustomModuleAttainment | null | undefined,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult | null | undefined,
    ): ModulePlanState | null {
        return getModulePlanState(module, validatablePlan, planValidationResult);
    }

    /**
     * The state of a module or a custom module attainment as one value for the UI: its attainment, the state of its module content
     * approval and its validation state mixed together.
     *
     * @deprecated Use getModulePlanState, which gives the validation state and the module content approval state separately, and
     * getModulePlanStateLabel to show them as one value.
     */
    static getPlanStateForModule(
        module: any,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        useContextualState: boolean = false,
    ): any {
        const planState = PlanValidationTs.getModulePlanState(module, validatablePlan, planValidationResult);
        if (!planState) {
            return null;
        }
        // A function, so that the attainment is checked only for the contextual state
        const isAttained = () => module.type === 'CustomModuleAttainment' || validatablePlan.isModuleAttained(module.id);
        return useContextualState && planState.contentApproval === 'NONE' && !isAttained()
            ? planValidationResult.moduleValidationResults[module.id].contextualState
            : getModulePlanStateLabel(planState);
    }

    /** @deprecated See getPlanStateForModule */
    static getContextualStateForModule(module: any, validatablePlan: ValidatablePlan, planValidationResult: PlanValidationResult) {
        return this.getPlanStateForModule(module, validatablePlan, planValidationResult, true);
    }
}
